import React from "react";
import {
  endMomentLabel,
  joinAfterStartShort,
  portfolioVisibilityShort,
  startingCashLabel,
  startMomentLabel,
  tradeLimitLabel,
} from "./leagueFormat";
import {
  ClockIcon,
  DollarCircleIcon,
  NoEntryIcon,
  SmallLockIcon,
  StopCircleIcon,
  UpDownArrowsIcon,
} from "./icons";

// The competition details block — the same six facts, laid out the same way, on
// the public league landing page and the private one.
//
// Client asked for it twice in one round, first for public and then for private:
// "Let's make this easy. Match the public league landing page layout. ... The
// details card should contain six items: STARTS / ENDS / STARTING CASH / TRADE
// LIMIT / JOIN AFTER START / PORTFOLIO VISIBILITY."
//
// So it is ONE component rather than the same six rows written out twice. The
// two landing pages have drifted apart before — that is what the "League details
// shows the wrong landing page" round was about — and six rows duplicated across
// two files is six more chances for it to happen again.

// One item: the uppercase eyebrow on the first line, the value on the second,
// and the outline glyph to the left of that whole text block.
//
// Client: "For every item, place the uppercase eyebrow label on the first line
// and its value on the second line. Both lines should share the same left
// margin. Place the corresponding outline icon to the left of the complete text
// block."
//
// The shared left margin is structural — both lines are children of one div and
// the glyph is that div's sibling — rather than two paddings that happen to
// agree today.
export const LeagueDetailRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-2.5 py-1.5">
    <span className="mt-0.5 shrink-0">{icon}</span>
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary">
        {label}
      </p>
      {/* Client: "Weight is the bigger lever here. Push the detail values to
          500/600 and keep the eyebrows where they are. Weight separates
          hierarchy without adding color, which keeps the card calm. Eyebrow
          label + icon stay quiet; no color or weight change."

          So the VALUE goes to 600 and nothing else moves — the eyebrow keeps its
          size, its tracking, its colour and its own 600, and the glyph keeps the
          muted grey. This was weight 400, which is why the label and the answer
          read as one flat block. */}
      <p className="mt-0.5 text-sm font-semibold text-[#0F172A]">{value}</p>
    </div>
  </div>
);

/**
 * The six items, in the client's order.
 *
 * DESKTOP: "Arrange the six items in two columns and three rows: Row 1: Starts |
 * Ends. Row 2: Starting cash | Trade limit. Row 3: Join after start | Portfolio
 * visibility."
 * MOBILE: "Stack all six items vertically in the same order."
 *
 * A plain two-column grid fills ROW-WISE, which is exactly the order above, so
 * one grid produces both layouts and the DOM order is the mobile order. Nothing
 * here chooses a layout in JS, so the order can never disagree with the width it
 * is read at.
 *
 * Starts and Ends stay conditional — a league with no stored end date has
 * nothing true to print — and the remaining items simply repack. The alternative
 * is an empty cell captioned ENDS.
 */
const LeagueDetailRows = ({ game, className = "" }) => {
  const startsAt = startMomentLabel(game);
  const endsAt = endMomentLabel(game);

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 md:gap-x-8 ${className}`}>
      {startsAt && (
        <LeagueDetailRow
          icon={<ClockIcon className="h-4 w-4" stroke="#9CA3AF" />}
          label="Starts"
          value={startsAt}
        />
      )}
      {endsAt && (
        <LeagueDetailRow
          icon={<StopCircleIcon className="h-4 w-4" stroke="#9CA3AF" />}
          label="Ends"
          value={endsAt}
        />
      )}
      {/* Client: "The glyphs next to Trade Limit and Starting Cash are green,
          while the other icons in this list are muted gray. Please change both
          to the same muted gray treatment as the other icons."

          They were green because both icons DEFAULT to #22A36B and these two
          call sites were the only ones in this block not passing a stroke — the
          four around them all pass #9CA3AF. Now all six do, so the row cannot
          drift again by someone adding a seventh.

          "For Trade Limit, also replace the current glyph with an up and down
          arrow, similar to the Buy ticket icon we use on mobile. Please keep it
          in the same icon family and visual style as the other glyphs."
          UpDownArrowsIcon is already in this family — same 1.8 stroke, same
          round caps and joins — and is the vertical up/down pair the mobile buy
          ticket uses. It replaces SwapArrowsIcon's two horizontal arrows. */}
      <LeagueDetailRow
        icon={<DollarCircleIcon className="h-4 w-4" stroke="#9CA3AF" />}
        label="Starting cash"
        value={startingCashLabel(game)}
      />
      <LeagueDetailRow
        icon={<UpDownArrowsIcon className="h-4 w-4" stroke="#9CA3AF" />}
        label="Trade limit"
        value={tradeLimitLabel(game)}
      />
      <LeagueDetailRow
        icon={<NoEntryIcon className="h-4 w-4" />}
        label="Join after start"
        value={joinAfterStartShort(!!game?.join_after_start)}
      />
      <LeagueDetailRow
        icon={<SmallLockIcon className="h-4 w-4" stroke="#9CA3AF" />}
        label="Portfolio visibility"
        value={portfolioVisibilityShort(!!game?.private_portfolio)}
      />
    </div>
  );
};

export default LeagueDetailRows;
